// 배경음악(mp3 파일 루프 재생)과 효과음(Web Audio API 합성)을 관리한다.
// 음소거 설정은 localStorage에 저장한다.

const STORAGE_KEY = 'tarot-sound-muted';

// 배경음악: public/audio 의 mp3 파일을 루프 재생한다.
const BG_SRC = `${process.env.PUBLIC_URL}/audio/whispers-of-destiny.mp3`;
const BG_VOLUME = 0.85;

let ctx = null;
let masterGain = null;
let bgAudio = null;
let muted = (() => {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
})();

function ensureContext() {
  if (ctx) return ctx;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  ctx = new AC();
  masterGain = ctx.createGain();
  masterGain.gain.value = muted ? 0 : 1;
  masterGain.connect(ctx.destination);
  return ctx;
}

function resumeIfNeeded() {
  if (ctx && ctx.state === 'suspended') {
    ctx.resume();
  }
}

// 짧은 효과음용 톤 한 줄을 만든다.
function blip(freq, { type = 'sine', dur = 0.4, gain = 0.18, delay = 0, glideTo = null } = {}) {
  const c = ensureContext();
  if (!c) return;
  resumeIfNeeded();

  const t0 = c.currentTime + delay;
  const osc = c.createGain ? c.createOscillator() : null;
  if (!osc) return;
  const g = c.createGain();

  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  if (glideTo) {
    osc.frequency.exponentialRampToValueAtTime(glideTo, t0 + dur);
  }

  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(gain, t0 + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);

  osc.connect(g);
  g.connect(masterGain);
  osc.start(t0);
  osc.stop(t0 + dur + 0.05);
}

// 배경음악 오디오 엘리먼트를 준비한다(한 번만 생성, 루프 재생).
function ensureBgAudio() {
  if (bgAudio) return bgAudio;
  if (typeof Audio === 'undefined') return null;
  bgAudio = new Audio(BG_SRC);
  bgAudio.loop = true;
  bgAudio.preload = 'auto';
  bgAudio.volume = BG_VOLUME;
  bgAudio.muted = muted;
  return bgAudio;
}

// 배경음악을 재생한다(브라우저 자동재생 정책상 사용자 제스처 이후 호출).
function startAmbient() {
  const a = ensureBgAudio();
  if (!a) return;
  a.muted = muted;
  const p = a.play();
  if (p && typeof p.catch === 'function') {
    p.catch(() => {
      /* 자동재생이 차단되면 다음 제스처에서 다시 시도된다 */
    });
  }
}

function stopAmbient() {
  if (bgAudio) {
    bgAudio.pause();
  }
}

// 카드를 뽑을 때: 부드러운 반짝임(샵 아르페지오).
function playCardSelect() {
  [587.33, 698.46, 880].forEach((f, i) =>
    blip(f, { type: 'triangle', dur: 0.5, gain: 0.26, delay: i * 0.06 })
  );
  blip(1760, { type: 'sine', dur: 0.6, gain: 0.1, delay: 0.04 });
}

// 해석을 시작할 때: 차오르는 신비로운 스웰.
function playInterpret() {
  blip(220, { type: 'sine', dur: 1.4, gain: 0.3, glideTo: 660 });
  blip(330, { type: 'triangle', dur: 1.4, gain: 0.16, glideTo: 990, delay: 0.05 });
}

function setMuted(value) {
  muted = value;
  try {
    localStorage.setItem(STORAGE_KEY, value ? '1' : '0');
  } catch {
    /* ignore */
  }
  // 배경음악(mp3) 음소거
  if (bgAudio) {
    bgAudio.muted = value;
  }
  // 효과음(Web Audio) 음소거
  if (masterGain && ctx) {
    masterGain.gain.cancelScheduledValues(ctx.currentTime);
    masterGain.gain.setValueAtTime(masterGain.gain.value, ctx.currentTime);
    masterGain.gain.linearRampToValueAtTime(value ? 0 : 1, ctx.currentTime + 0.3);
  }
}

function isMuted() {
  return muted;
}

const soundManager = {
  startAmbient,
  stopAmbient,
  playCardSelect,
  playInterpret,
  setMuted,
  isMuted,
};

export default soundManager;
